import {
  signInWithCustomToken,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  User as FirebaseUser,
  updateProfile,
  GoogleAuthProvider,
  signInWithPopup,
} from 'firebase/auth';
import { auth } from './config';
import { doc, getDoc, setDoc, collection, query, where, limit, getDocs, updateDoc } from 'firebase/firestore';
import { db } from './config';

export interface AgentData {
  userId: string;
  displayName?: string;
  tonAddress?: string;
  createdAt?: Date;
}

export interface UserData {
  id: string;
  email?: string;
  telegramUserId?: string;
  createdAt?: Date;
  agent?: AgentData;
  /** Fin de la formule Pro (absent = jamais abonné). Pro actif tant que la date est dans le futur. */
  proUntil?: Date;
}

// Inscription avec email et mot de passe
export const signUpWithEmail = async (email: string, password: string, displayName?: string) => {
  try {
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    const user = userCredential.user;

    // Mettre à jour le profil avec le nom d'affichage
    if (displayName) {
      await updateProfile(user, { displayName });
    }

    // Créer le document utilisateur dans Firestore
    const userRef = doc(db, 'users', user.uid);
    await setDoc(userRef, {
      email: email,
      createdAt: new Date(),
    });

    // Créer l'agent associé
    const agentRef = doc(collection(db, 'agents'));
    await setDoc(agentRef, {
      userId: user.uid,
      displayName: displayName || email.split('@')[0],
      tonAddress: null,
      createdAt: new Date(),
    });

    return user;
  } catch (error: any) {
    console.error('Sign up error:', error);
    throw error;
  }
};

// Connexion avec email et mot de passe
export const signInWithEmail = async (email: string, password: string) => {
  try {
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    return userCredential.user;
  } catch (error: any) {
    console.error('Sign in error:', error);
    throw error;
  }
};

// Connexion avec Google
export const signInWithGoogle = async () => {
  try {
    const provider = new GoogleAuthProvider();
    const userCredential = await signInWithPopup(auth, provider);
    const user = userCredential.user;

    // Vérifier si l'utilisateur existe déjà dans Firestore
    const userRef = doc(db, 'users', user.uid);
    const userSnap = await getDoc(userRef);

    if (!userSnap.exists()) {
      // Créer le document utilisateur dans Firestore pour un nouvel utilisateur
      await setDoc(userRef, {
        email: user.email,
        createdAt: new Date(),
      });

      // Créer l'agent associé
      const agentRef = doc(collection(db, 'agents'));
      await setDoc(agentRef, {
        userId: user.uid,
        displayName: user.displayName || user.email?.split('@')[0] || 'User',
        tonAddress: null,
        createdAt: new Date(),
      });
    }

    return user;
  } catch (error: any) {
    console.error('Google sign in error:', error);
    throw error;
  }
};

// Authentification via Telegram WebApp
export const authenticateWithTelegram = async (telegramData: any) => {
  try {
    // Appeler le backend pour obtenir un custom token Firebase
    const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3000';
    const response = await fetch(`${apiUrl}/auth/telegram-token`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(telegramData),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Failed to get Firebase token' }));
      throw new Error(error.message || 'Failed to get Firebase token');
    }

    const { customToken } = await response.json();

    if (!customToken) {
      throw new Error('No custom token received');
    }

    // Utiliser le custom token pour s'authentifier avec Firebase
    const userCredential = await signInWithCustomToken(auth, customToken);
    return userCredential.user;
  } catch (error: any) {
    console.error('Authentication error:', error);
    throw error;
  }
};

// Authentification via Telegram Mini App (initData signée par Telegram)
export const authenticateWithTelegramWebApp = async (initData: string) => {
  try {
    const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3000';
    const response = await fetch(`${apiUrl}/auth/telegram-webapp-token`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ initData }),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Failed to get Firebase token' }));
      throw new Error(error.message || 'Failed to get Firebase token');
    }

    const { customToken } = await response.json();
    if (!customToken) {
      throw new Error('No custom token received');
    }

    const userCredential = await signInWithCustomToken(auth, customToken);
    return userCredential.user;
  } catch (error: any) {
    console.error('Telegram Mini App authentication error:', error);
    throw error;
  }
};

// Lier un compte Telegram à un utilisateur existant (depuis la Mini App : initData signée)
export const linkTelegramAccount = async (initData: string) => {
  try {
    const currentUser = auth.currentUser;
    if (!currentUser) {
      throw new Error('User not authenticated');
    }

    // Appeler le backend pour valider et lier le compte Telegram
    const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3000';
    const response = await fetch(`${apiUrl}/auth/link-telegram-webapp`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${await currentUser.getIdToken()}`,
      },
      body: JSON.stringify({ initData }),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Failed to link Telegram account' }));
      throw new Error(error.message || 'Failed to link Telegram account');
    }

    // Le backend a déjà mis à jour Firestore, on n'a pas besoin de le faire ici
    // Mais on peut recharger les données utilisateur
    return true;
  } catch (error: any) {
    console.error('Link Telegram error:', error);
    throw error;
  }
};

// Liaison rapide via le bot : génère un code à usage unique et renvoie le deep link t.me
export const requestTelegramLinkViaBot = async (): Promise<{ code: string; url: string; expiresAt: string }> => {
  const currentUser = auth.currentUser;
  if (!currentUser) {
    throw new Error('User not authenticated');
  }

  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3000';
  const response = await fetch(`${apiUrl}/auth/telegram-link-code`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${await currentUser.getIdToken()}`,
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Failed to create Telegram link code' }));
    throw new Error(error.message || 'Failed to create Telegram link code');
  }

  const { code, startParam, expiresAt } = await response.json();
  const botUsername = (process.env.REACT_APP_TELEGRAM_BOT_USERNAME || 'stars_paid_bot').replace(/^@/, '');
  return {
    code,
    expiresAt,
    url: `https://t.me/${botUsername}?start=${encodeURIComponent(startParam)}`,
  };
};

// Déconnexion
export const logoutUser = async () => {
  try {
    await signOut(auth);
  } catch (error) {
    console.error('Logout error:', error);
    throw error;
  }
};

// Obtenir les données utilisateur depuis Firestore
export const getUserData = async (userId: string): Promise<UserData | null> => {
  try {
    const userRef = doc(db, 'users', userId);
    const userSnap = await getDoc(userRef);

    if (!userSnap.exists()) {
      return null;
    }

    const userData = userSnap.data();
    
    // Récupérer les données de l'agent (agents.userId référence users.id)
    const agentsQuery = query(
      collection(db, 'agents'),
      where('userId', '==', userId),
      limit(1)
    );
    const agentSnapshot = await getDocs(agentsQuery);

    let agent: AgentData | undefined;
    if (!agentSnapshot.empty) {
      const agentDoc = agentSnapshot.docs[0];
      const agentData = agentDoc.data();
      agent = {
        userId: agentDoc.id,
        displayName: agentData.displayName,
        tonAddress: agentData.tonAddress,
        createdAt: agentData.createdAt?.toDate(),
      };
    }

    return {
      id: userSnap.id,
      email: userData.email,
      telegramUserId: userData.telegramUserId,
      createdAt: userData.createdAt?.toDate(),
      agent,
      proUntil: userData.proUntil?.toDate?.(),
    };
  } catch (error) {
    console.error('Error getting user data:', error);
    return null;
  }
};

// Observer les changements d'authentification
export const onAuthStateChange = (callback: (user: FirebaseUser | null) => void) => {
  return onAuthStateChanged(auth, callback);
};

// Obtenir l'utilisateur actuel
export const getCurrentUser = () => {
  return auth.currentUser;
};
